import { Link } from "react-router-dom";
import { ArrowRight, UserRound } from "lucide-react";
import { Marquee } from "@/components/Marquee";
import { Reveal } from "@/components/Reveal";
import { SERVICES, IMAGES } from "@/data/content";

const SERVICE_IMAGES = {
  Boarding: IMAGES.hero,
  "Day Care": IMAGES.playGroup,
  Grooming: IMAGES.grooming,
  Training: IMAGES.portrait,
  Events: IMAGES.pomeranian,
  Vaccinations: IMAGES.bowl,
};

export default function Services() {
  return (
    <>
      <section className="pt-32 pb-16 lg:pt-40 lg:pb-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal y={14}>
            <p className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.3em] text-bark">
              <span className="h-px w-10 bg-bark" aria-hidden="true" />
              Our services
            </p>
          </Reveal>
          <h1 className="mt-6 font-display text-4xl font-medium leading-[1.08] tracking-tight text-ink sm:text-5xl lg:text-6xl">
            <span className="mask-line">
              <span style={{ animationDelay: "0.15s" }}>Everything your pup needs,</span>
            </span>
            <span className="mask-line">
              <span style={{ animationDelay: "0.28s" }}>
                <em className="font-editorial italic text-bark">under one woof.</em>
              </span>
            </span>
          </h1>
          <Reveal delay={0.5}>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg">
              Six services, one philosophy — delivered by trained staff in a facility designed
              around hygiene, comfort and every temperament.
            </p>
          </Reveal>
        </div>
      </section>

      <Marquee />

      <section className="mx-auto max-w-7xl space-y-24 px-4 py-24 sm:px-6 lg:px-8 lg:py-32 lg:space-y-32">
        {SERVICES.map((s, i) => {
          const Icon = s.icon;
          const flip = i % 2 === 1;
          return (
            <article
              key={s.name}
              data-testid={`service-detail-${s.name.toLowerCase().replace(/\s+/g, "-")}`}
              className="grid items-center gap-10 lg:grid-cols-2 lg:gap-20"
            >
              <Reveal className={flip ? "lg:order-2" : ""}>
                <img
                  src={SERVICE_IMAGES[s.name]}
                  alt={`${s.name} at the facility`}
                  className="aspect-[4/3] w-full rounded-[2rem] object-cover ring-1 ring-ink/10"
                />
              </Reveal>
              <div className={flip ? "lg:order-1" : ""}>
                <Reveal>
                  <div className="flex items-center gap-4">
                    <span className="rounded-full bg-bark-soft p-3 text-bark">
                      <Icon className="h-6 w-6" aria-hidden="true" />
                    </span>
                    <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-400">
                      {String(i + 1).padStart(2, "0")} — {s.tag}
                    </p>
                  </div>
                  <h2 className="mt-4 font-display text-3xl font-medium tracking-tight text-ink sm:text-4xl">
                    {s.name}
                  </h2>
                </Reveal>
                <Reveal delay={0.12}>
                  <p className="mt-5 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg">
                    {s.detail}
                  </p>
                </Reveal>
                <Reveal delay={0.2}>
                  <Link
                    to="/signin"
                    data-testid={`service-book-${s.name.toLowerCase().replace(/\s+/g, "-")}`}
                    className="group mt-8 inline-flex items-center gap-2 rounded-full bg-bark px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-bark-dark"
                  >
                    Login to book {s.name.toLowerCase()}
                    <ArrowRight
                      className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1"
                      aria-hidden="true"
                    />
                  </Link>
                </Reveal>
              </div>
            </article>
          );
        })}
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-24 sm:px-6 lg:px-8 lg:pb-32">
        <Reveal>
          <div
            data-testid="services-login-nudge"
            className="relative overflow-hidden rounded-[2rem] bg-navy px-7 py-16 text-center sm:px-12"
          >
            <div
              aria-hidden="true"
              className="absolute -left-24 -top-24 h-72 w-72 rounded-full bg-bark/15 blur-3xl"
            />
            <h2 className="relative font-display text-3xl font-medium tracking-tight text-cream sm:text-4xl">
              Ready to plan your pup's{" "}
              <em className="font-editorial italic text-amberwarm">next stay?</em>
            </h2>
            <p className="relative mx-auto mt-4 max-w-xl text-base text-cream/60">
              The customer portal launches soon — profiles, records and bookings in one place.
            </p>
            <Link
              to="/signin"
              data-testid="services-login-cta"
              className="relative mt-9 inline-flex items-center gap-2 rounded-full bg-bark px-8 py-4 text-sm font-semibold text-white transition-colors duration-200 hover:bg-bark-dark"
            >
              <UserRound className="h-4 w-4" aria-hidden="true" />
              Go to customer login
            </Link>
          </div>
        </Reveal>
      </section>
    </>
  );
}
